import clsx from "clsx"
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { useCallback, useEffect, useRef, useState } from "react"
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNavigate } from "react-router-dom"
import { keycloak } from "../../../lib/keycloak/index.ts"
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hook.ts"
import { colors } from "../../../ui/colors.ts"
import { ArrowRightIcon } from "../../../ui/icons/arrowRightIcon.tsx"
import { BotIcon } from "../../../ui/icons/botIcon.tsx"
import { BreadcrumbsIcon } from "../../../ui/icons/breadcrumbIcon.tsx"
import { EditIcon } from "../../../ui/icons/editIcon.tsx"
import LampIcon from "../../../ui/icons/lampIcon.tsx"
import { ButtonComponent } from "../../../ui/molecules/button/index.tsx"
import { SpinnerComponent } from "../../../ui/molecules/spinner/index.tsx"
import { ErrorPopup } from "../../../ui/organisms/errorPopup/index.tsx"
import { InfoTextPopup } from "../../../ui/organisms/infoTextPopup/index.tsx"
import { SubscriptionTierLimit } from "../../financialAdvisor/dto.ts"
import { setSelectedTab } from "../../financialAdvisor/slice.ts"
import BrightyStartingPage from "./brighty/brightyStartingPage.tsx"
import { BrightyMessage } from "./brighty/dto.ts"
import BrightyWebSocket from './brighty/index.tsx'
import { findBrightyChatById, findBrightyChats, setAdded, setFindBrightyChatByIdStatus, setFromHome, setMenuClosed, setPrecision, setSelectedChat, setStateProgress, setStateStage, setTextMessage, setWritten } from "./brighty/slice.ts"
import { NewMessageBotDTO } from "./dto.ts"
import { MessageBotComponent } from "./messageBot.tsx"
import { findChatBot, findChatBotById, questionBot, setFindChatBotByIdStatus, setQuestionBotStatus } from "./slice.ts"
import "./style.css"

export function ChatBotComponent() {
    const layoutState = useAppSelector(state => state.layout)
    const authState = useAppSelector(state => state.auth)
    const companyState = useAppSelector(state => state.company)
    const brightyState = useAppSelector(state => state.brighty)
    const financialAdvisorState = useAppSelector(state => state.financialAdvisor)
    const chatState = useAppSelector(state => state.chat)
    const dispatch = useAppDispatch()

    const navigate = useNavigate()

    const [messagesBot, setMessagesBot] = useState<NewMessageBotDTO[]>([])
    const [errorStatus, setErrorStatus] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null);
    const spanRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const messageBotRef = useRef<HTMLDivElement>(null)
    const [progress, setProgress] = useState(0);
    const [stage, setStage] = useState('');
    const [message, setMessage] = useState('');
    const [wrote, setWrote] = useState(false)
    const [pageBot, setPageBot] = useState(0)

    const [triedToAdd, setTriedToAdd] = useState(false)

    const [isConnected, setIsConnected] = useState(false);
    const clientId = brightyState.filters.user ? brightyState.filters.user : ''
    const wsRef = useRef<any>(null);
    const wsInstanceRef = useRef<any>(null);

    const fa = keycloak.hasRealmRole('company') ? financialAdvisorState.findByIdFinancialAdvisorResponse! : authState.findMeFinancialAdvisorResponse!

    const chats = brightyState.findBrightyChatResponse ? brightyState.findBrightyChatResponse.messages : { messages: {} }
    const companyChats = chatState.findChatBotResponse ? chatState.findChatBotResponse.messages : { messages: {} }
    const maxChat = brightyState.selectedChat >= 11
    const canSendMessage = fa ?
        fa.subscriptionTierLimit !== SubscriptionTierLimit.free ? true :
            (brightyState.selectedChat === Object.keys(keycloak.hasRealmRole('company') ? companyChats : chats).length + 1 ? true : messagesBot.length <= 2) : false
    const canOpenNewChat = fa ? fa.subscriptionTierLimit !== SubscriptionTierLimit.free ? true : (Object.keys(keycloak.hasRealmRole('company') ? companyChats : chats).length < 10) : false
    const reachedLimit = fa ? fa.subscriptionTierLimit !== SubscriptionTierLimit.free ? false : brightyState.selectedChat >= 11 : true

    function sendMessage() {
        if (
            keycloak.tokenParsed !== undefined &&
            keycloak.tokenParsed.exp !== undefined &&
            brightyState.textMessage !== '' &&
            canSendMessage && !reachedLimit
        ) {

            setWrote(true)
            setMessagesBot((prevMessages) => [
                ...prevMessages,
                {
                    user_id: keycloak.hasRealmRole('company')
                        ? authState.findMeCompanyResponse?.userId!
                        : authState.findMeFinancialAdvisorResponse?.userId!,
                    content: brightyState.textMessage,
                    response: '',
                },
            ]);
            if (keycloak.hasRealmRole('financial_advisor')) {
                handleSubmit();
            }
            else {
                dispatch(questionBot({
                    question: brightyState.textMessage,
                    precision: 0.5,
                    thread_id: brightyState.selectedChat.toString()
                }))
            }
            dispatch(setTextMessage(''));
        }
    }

    const penultimateMessageRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (brightyState.findBrightyChatsStatus === 'failed') { 
            window.location.reload()
        }
    }, [brightyState.findBrightyChatsStatus])

    useEffect(() => {
        if (messagesBot.length >= 1 && penultimateMessageRef.current) {
            penultimateMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [messagesBot]);

    useEffect(() => {
        if (brightyState.fromHome && reachedLimit) {
            setTriedToAdd(true)
            dispatch(setTextMessage(''))
            dispatch(setFromHome(false))
            return
        }
        if (brightyState.fromHome && brightyState.selectedChat !== 0 && isConnected) {
            sendMessage()
        }
        const id = setInterval(() => {
            if (!isConnected && wsInstanceRef !== null && wsInstanceRef.current !== null && wsInstanceRef.current.connectWebSocket) {
                wsInstanceRef.current.connectWebSocket()
            }
        }, 5000);
        return () => clearInterval(id)
    }, [isConnected])

    useEffect(() => {
        if (brightyState.fromHome) {
            if (reachedLimit) {
                dispatch(setFromHome(false))
                return
            }
            if (keycloak.hasRealmRole('company'))
                sendMessage()
        }
    }, [brightyState.fromHome])

    useEffect(() => {
        if (!brightyState.fromHome) {
            setMessagesBot([])
            dispatch(setTextMessage(""))
        }
        dispatch(setPrecision(0.5))
        setWrote(false)
    }, [brightyState.selectedChat])

    useEffect(() => {
        if (inputRef.current && spanRef.current) {
            const inputWidth = inputRef.current.offsetWidth;
            const spanWidth = spanRef.current.scrollWidth;
            setIsOverflowing(spanWidth > inputWidth);
        }
    }, [brightyState.textMessage]);

    const handleSubmit = useCallback(() => {

        if (wsInstanceRef.current?.sendMessage) {
            wsInstanceRef.current.sendMessage(
                brightyState.textMessage,
                brightyState.precision,
                clientId,
                brightyState.fromHome ? (Object.keys(keycloak.hasRealmRole('company') ? companyChats : chats).length + 1).toString() : brightyState.selectedChat.toString()
            );

            dispatch(setWritten(true))
            setMessage('');
            setProgress(0);
            dispatch(setStateProgress(0))
            setStage('');
        } else {
            console.error('WebSocket connection not available');
        }
    }, [brightyState.textMessage, brightyState.precision, clientId]);

    const handleWebSocketMessage = useCallback((wsMessage: {
        type?: string;
        stage?: string;
        message?: string;
        progress?: number;
        data?: {
            id: string
            response: string
        }
    }) => {
        if (wsMessage.type === 'progress') {
            if (wsMessage.progress !== undefined) {
                setProgress(wsMessage.progress);
                dispatch(setStateProgress(wsMessage.progress))
            }

            if (wsMessage.stage) {
                dispatch(setStateStage(wsMessage.stage))
                setStage(wsMessage.stage);
            }
        }
        if (wsMessage.message) {
            setMessage(wsMessage.message);
        }
        if (wsMessage.data) {
            if (typeof wsMessage.data.response === "string") {
                const match = wsMessage.data?.response.match(/['"]extended_response['"]\s*:\s*['"]([^]*?)['"](?=,|\})/);

                if (match && match[1]) {
                    const extendedResponse = match[1];
                    setMessage(extendedResponse);
                } else {
                    console.error('extended_response non trovato');
                }
            }
            else {
                //@ts-ignore
                setMessage(wsMessage.data.response.extended_response)
            }
            dispatch(findBrightyChatById({ id: brightyState.fromHome ? (Object.keys(chats).length + 1).toString() : brightyState.selectedChat.toString() }))
            dispatch(setWritten(false))
            dispatch(setFromHome(false))
        }
    }, []);

    const handleConnect = useCallback(() => {
        setIsConnected(true);
    }, []);

    const handleDisconnect = useCallback(() => {
        setIsConnected(false);
    }, []);

    const setWsInstance = useCallback((instance: any) => {
        if (instance) {
            wsInstanceRef.current = instance;
        }
    }, []);

    useEffect(() => {
        if (progress === 100) {
            dispatch(findBrightyChats(brightyState.filters))
            dispatch(setWritten(false))
        }
    }, [progress])

    useEffect(() => {
        if (brightyState.findBrightyChatByIdStatus === 'successfully' && brightyState.findBrightyChatByIdResponse) {
            if (!brightyState.fromHome)
                setMessagesBot([])
            setErrorStatus(false)
            dispatch(setFindBrightyChatByIdStatus("idle"))
            const messages = brightyState.findBrightyChatByIdResponse.messages
                .map((item, index) => {
                    if (item.type !== 'HumanMessage') return null;
                    return {
                        user_id: item.type === 'HumanMessage' ? keycloak.hasRealmRole('company') ? authState.findMeCompanyResponse?.userId : authState.findMeFinancialAdvisorResponse?.userId : 'Brighty',
                        content: item.content as string,
                        response: brightyState.findBrightyChatByIdResponse!.messages[index + 1] ? typeof brightyState.findBrightyChatByIdResponse!.messages[index + 1].content === 'string' ?
                            brightyState.findBrightyChatByIdResponse!.messages[index + 1].content :
                            (
                                brightyState.findBrightyChatByIdResponse!.messages[index + 1]
                                    ?.content as BrightyMessage
                            )?.extended_response || "Mi dispiace. C'è stato un errore." : "Mi dispiace. C'è stato un errore."
                    };
                })
                .filter((item): item is { user_id: string; content: string; response: string } => item !== null);
            const html = messages.map(({ user_id, content, response }) => {
                const rawHTML = marked(response.replace(/\[\d+\]/g, '')) as string;
                const cleanHTML = DOMPurify.sanitize(rawHTML);

                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = cleanHTML;

                const links = tempDiv.querySelectorAll('a');
                links.forEach(link => {
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                });

                return {
                    user_id,
                    content,
                    response: tempDiv.innerHTML
                };
            })

            let messagesList = [...html]
            if (!brightyState.fromHome)
                setMessagesBot(messagesList)
            const id = setTimeout(() => {
                setWrote(false)
            }, 5000);
            return () => clearTimeout(id)
        }
        else if (brightyState.findBrightyChatByIdStatus === 'failed') {
            setErrorStatus(true)
        }
    }, [brightyState.findBrightyChatByIdStatus])

    useEffect(() => {
        if (
            chatState.findChatBotByIdStatus === 'successfully' &&
            chatState.findChatBotByIdResponse !== undefined
        ) {
            setErrorStatus(false)
            dispatch(setFindChatBotByIdStatus("idle"))

            const messages = chatState.findChatBotByIdResponse.messages
                .map((item, index) => {
                    if (item.type !== 'HumanMessage') return null;
                    const response = chatState.findChatBotByIdResponse!.messages[index + 1] ? (typeof chatState.findChatBotByIdResponse!.messages[index + 1].content === 'string' ?
                        chatState.findChatBotByIdResponse!.messages[index + 1].content :
                        (
                            chatState.findChatBotByIdResponse!.messages[index + 1]
                                ?.content as BrightyMessage
                        )?.extended_response) || "Mi dispiace. C'è stato un errore." : "Mi dispiace. C'è stato un errore."
                    return {
                        user_id: item.type === 'HumanMessage' ? keycloak.hasRealmRole('company') ? authState.findMeCompanyResponse?.userId : authState.findMeFinancialAdvisorResponse?.userId : 'Brighty',
                        content: item.content as string,
                        response: response +
                            '\n\n' +
                            //@ts-ignore
                            (Array.isArray(chatState.findChatBotByIdResponse!.messages[index + 1].content.citations) ?
                                //@ts-ignore
                                `**Fonti:**<br/>${chatState.findChatBotByIdResponse!.messages[index + 1].content.citations.map((elem, index) => `<a href=${elem} target="_blank" rel="noopener noreferrer">` + (elem.length <= 50 ? elem : elem.substring(0, 50).concat('...')) + '</a>').join('<br/>')}`
                                : '')
                    };
                })
                .filter((item): item is { user_id: string; content: string; response: string } => item !== null);
            setMessagesBot(messages)
            dispatch(setFromHome(false))
            const id = setTimeout(() => {
                setWrote(false)
            }, 5000);
            return () => clearTimeout(id)
        }
    }, [chatState.findChatBotByIdStatus])

    useEffect(() => {
        if (chatState.questionBotStatus === "successfully") {
            setTextMessage("")
            dispatch(findChatBot({ page: 0, page_size: 500, request_user_id: authState.findMeCompanyResponse?.userId!, financial_advisor_user_id: financialAdvisorState.findByIdFinancialAdvisorResponse?.userId! }))
            dispatch(findChatBotById({ thread_id: brightyState.selectedChat.toString(), user_id: brightyState.filters.user! }))
            dispatch(setQuestionBotStatus("idle"))
        } else if (chatState.questionBotStatus === "failed") {
            setErrorStatus(true)
            setMessagesBot(prevMessages => {
                const updatedMessages = prevMessages.slice(1);
                return [{
                    user_id: keycloak.hasRealmRole("company") ? authState.findMeCompanyResponse?.userId! : authState.findMeReferrersResponse?.userId!,
                    date: new Date(),
                    content: brightyState.textMessage,
                    response: "Ops, c'è stato un problema! Riprova tra un attimo."
                }, ...updatedMessages];
            });
            const id = setTimeout(() => {
                setMessagesBot([])
                dispatch(findChatBot({ page: 0, page_size: 500, request_user_id: authState.findMeCompanyResponse?.userId!, financial_advisor_user_id: financialAdvisorState.findByIdFinancialAdvisorResponse?.userId! }))
                dispatch(setQuestionBotStatus("idle"))
            }, 4000)

            return () => clearTimeout(id)
        }
    }, [chatState.questionBotStatus])

    return (
        <div className={clsx(" h-full bg-white w-full transition-all ease-in-out duration-1000  flex flex-col justify-between",
            {
                "rounded-xl": brightyState.menuClosed,
                "rounded-r-xl": !brightyState.menuClosed
            }
        )}>
            <ErrorPopup cta={keycloak.hasRealmRole('company') ? <></> : <span className=" cursor-pointer text-warning-400 font-semibold text-text-md" onClick={() => {
                dispatch(setSelectedTab(2));
                navigate('/setting')
            }}>Aggiorna il piano</span>} message="Hai raggiunto il numero max. di chat!" active={triedToAdd} close={() => setTriedToAdd(false)} />
            {
                brightyState.selectedChat === 0 ? (
                    <BrightyStartingPage />
                ) : (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div className=" border-b border-b-neutral-100 flex items-center justify-between p-4" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="flex flex-row items-center gap-3 flex-grow">
                                <div className=" flex flex-row gap-1.5">
                                    {brightyState.menuClosed && <div onClick={() => {
                                        dispatch(setMenuClosed(!brightyState.menuClosed))
                                    }} className={
                                        clsx(
                                            'transition-all cursor-pointer hover:bg-neutral-200 p-1.5 rounded-xl flex items-center justify-center ',
                                            {
                                                'rotate-0 ': !brightyState.menuClosed,
                                                'rotate-180': brightyState.menuClosed,
                                            }
                                        )
                                    }>
                                        <BreadcrumbsIcon size={'24'} color={colors.brandSecondary[500]} />
                                    </div>}
                                    {brightyState.menuClosed && <div onClick={() => {
                                        if (!brightyState.written && (keycloak.hasRealmRole('company') ? chatState.findChatBotResponse : brightyState.findBrightyChatResponse) && canOpenNewChat) {
                                            dispatch(setAdded(true))
                                            dispatch(setSelectedChat(Object.keys(keycloak.hasRealmRole('company') ? companyChats : chats).length + 1))
                                        }
                                        else if (!canOpenNewChat)
                                            setTriedToAdd(true)
                                    }} className=' cursor-pointer hover:bg-neutral-200 p-1.5 rounded-xl flex items-center justify-center'>
                                        <EditIcon
                                            size={'24'} color={colors.brandSecondary[500]} />
                                    </div>
                                    }
                                </div>
                                <div className="w-[48px] h-[48px] rounded-[240px] flex justify-center items-center" >
                                    <BotIcon size={""} color={''} />
                                </div>
                                <div className="flex flex-col gap-1 w-full ">
                                    <div className="  flex gap-8 items-center justify-between">
                                        <div className=" flex flex-col gap-2">
                                            <div className=" flex flex-row gap-2 items-center">
                                                <span className="text-text-md font-bold text-neutral-800">
                                                    Brighty
                                                </span>
                                                {
                                                    keycloak.hasRealmRole('financial_advisor') &&
                                                    <div style={{ backgroundColor: layoutState.theme[500] }} className={'  outline-white outline p-1 rounded-xl w-10 flex items-center justify-center'}><span className=' text-[10px] leading-4 text-white'>PRO</span></div>
                                                }
                                            </div>
                                            {(keycloak.hasRealmRole('company') && (fa && fa.subscriptionTierLimit === SubscriptionTierLimit.free)) && <span className=" text-xs text-neutral-500">Questa versione di Brighty è limitata a 10 conversazioni da 3 messaggi ciascuna.
                                                <br /> Vuoi scoprire tutto il suo potenziale? Parla con il tuo commercialista per attivare la versione completa!</span>}
                                        </div>
                                        <span className=" text-neutral-500 text-text-xs text-right">
                                            Brighty può commettere errori. <br /> Assicurati di verificare le informazioni importanti.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className="newChat"
                            style={{
                                position: 'relative',
                                height: 'calc(100% - 147.98px)',
                                overflowY: 'auto'
                            }}
                        >
                            {
                                brightyState.findBrightyChatByIdStatus === 'loading' ?
                                    <div className="flex justify-center items-center h-full">
                                        <SpinnerComponent />
                                    </div>
                                    :
                                    <div className=" scrollbar-hidden flex flex-col-reverse overflow-scroll h-full" id='scrollableDivMessages' ref={messageBotRef}>
                                        <InfiniteScroll
                                            key={brightyState.selectedChat + '-chat-bot-list-message'}
                                            inverse
                                            style={{ display: 'flex', flexDirection: 'column', margin: 'auto', marginBottom: 4, marginTop: 8, width: '80%' }}
                                            scrollableTarget="scrollableDivMessages"
                                            dataLength={messagesBot.length}
                                            next={() => {
                                                if (chatState.findChatBotStatus !== 'loading' && keycloak.hasRealmRole('company')) {
                                                    setPageBot(pageBot + 1)
                                                    dispatch(findChatBot({ page: pageBot + 1, page_size: 500, request_user_id: authState.findMeCompanyResponse?.userId!, financial_advisor_user_id: financialAdvisorState.findByIdFinancialAdvisorResponse?.userId! }))
                                                }
                                            }}
                                            hasMore={false}
                                            endMessage={
                                                <span className='text-text-md font-semibold text-neutral-800 text-center p-4'>
                                                    {''}
                                                </span>
                                            }
                                            loader={
                                                <div
                                                    className=" w-full p-2.5 m-0 flex items-center justify-center"
                                                >
                                                    <SpinnerComponent />
                                                </div>
                                            }
                                        >
                                            {messagesBot.length > 0 &&
                                                messagesBot.map((message, index) => {
                                                    const isPenultimate = index === messagesBot.length - 1;

                                                    return (
                                                        <div
                                                            ref={isPenultimate ? penultimateMessageRef : null}
                                                            key={message.user_id + index.toString()}
                                                            className="flex gap-2 flex-col items-center justify-between self-stretch rounded-[6px] w-full"
                                                            style={{
                                                                padding: (
                                                                    (
                                                                        messagesBot[index + 1] &&
                                                                        messagesBot[index + 1].user_id === message.user_id
                                                                    ) ? '4px 32px' : '8px 32px')
                                                            }}>
                                                            <div
                                                                className="text-text-lg flex-grow max-w-[75%] font-medium text-neutral-800"
                                                                style={{
                                                                    alignSelf: message.user_id === keycloak.subject ? 'flex-end' : 'flex-start'
                                                                }}
                                                            >
                                                                <MessageBotComponent
                                                                    message={message.content}
                                                                    sent={(keycloak.hasRealmRole("company") || keycloak.hasRealmRole('financial_advisor')) ? true : false}
                                                                    sender={(keycloak.hasRealmRole("company") || keycloak.hasRealmRole('financial_advisor')) ? "" : companyState.findByIdCompanyResponse?.companyName.trim() ? companyState.findByIdCompanyResponse?.companyName : (companyState.findByIdCompanyResponse?.name + " " + companyState.findByIdCompanyResponse?.surname)}
                                                                    sameSanderAsPrevious={
                                                                        index > 0 &&
                                                                        messagesBot[index - 1].user_id === message.user_id
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="text-text-lg font-medium text-neutral-800"
                                                                style={{
                                                                    flexGrow: 1,
                                                                    flexBasis: '50%',
                                                                    maxWidth: '50%',
                                                                    alignSelf: 'flex-start'
                                                                }}
                                                            >
                                                                <MessageBotComponent
                                                                    last={index === messagesBot.length - 1 && wrote}
                                                                    message={message.response}
                                                                    sent={false}
                                                                    sender="Brighty"
                                                                    sameSanderAsPrevious={
                                                                        index > 0 &&
                                                                        messagesBot[index - 1].user_id === message.user_id
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                                )
                                            }
                                            {messagesBot.length === 0 &&
                                                <BrightyStartingPage />
                                            }
                                        </InfiniteScroll>
                                    </div>
                            }
                        </div>
                        <div className=" px-8 pt-4 pb-8 gap-2 flex items-center m-auto h-[72px] w-[80%]">
                            <div
                                onClick={(e) => {
                                    e.currentTarget.querySelector('input')?.focus();
                                }}
                                className="h-14 border relative border-neutral-200 bg-neutral-100 py-3 px-2.5 pl-6 gap-2 rounded-xl flex flex-row w-full items-center justify-between"
                            >
                                <div className="flex-1 relative overflow-x-hidden pr-[10px]">
                                    <input
                                        ref={inputRef}
                                        disabled={brightyState.written || !canSendMessage}
                                        onKeyDown={(e) => {
                                            if (maxChat && fa && fa.subscriptionTierLimit === SubscriptionTierLimit.free) {
                                                setTriedToAdd(true)
                                                dispatch(setTextMessage(''))
                                                return
                                            }
                                            if (e.key === 'Enter')
                                                sendMessage()
                                        }}
                                        onChange={(e) => dispatch(setTextMessage(e.currentTarget.value))}
                                        id="chat"
                                        value={brightyState.textMessage}
                                        className="bg-transparent focus:ring-0 outline-none ring-0 font-medium text-text-md leading-6 text-neutral-600 focus:border-none w-full whitespace-nowrap"
                                        placeholder={canSendMessage ? "Inserisci un messaggio" : "Hai raggiunto il numero max. di messaggi per questa chat"}
                                        style={{
                                            minWidth: '100%',
                                        }}
                                    />
                                    {(isOverflowing) && <div
                                        className=" absolute right-0 top-0 h-full w-[60px] pointer-events-none "
                                        style={{
                                            background: 'linear-gradient(90deg,rgba(242, 242, 250, 0) 0%, rgba(242, 242, 250, 1) 100%)',
                                        }}
                                    />}
                                    <span
                                        ref={spanRef}
                                        className="invisible absolute whitespace-nowrap font-medium text-text-md leading-6"
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        {brightyState.textMessage}
                                    </span>
                                </div>
                                <div className="flex flex-row gap-2 items-center">
                                    {!keycloak.hasRealmRole('company') && <InfoTextPopup
                                        label="Attiva per risposte più dettagliate e ragionate"
                                        position="top"
                                        color="blue"
                                    >
                                        <div
                                            onClick={() =>
                                                dispatch(setPrecision(brightyState.precision === 0.5 ? 0.9 : 0.5))
                                            }
                                            className={clsx(
                                                'cursor-pointer rounded-full border py-2 px-4 flex gap-2 items-center',
                                                {
                                                    'border bg-white border-brandPrimary-300 hover:bg-brandSecondary-100':
                                                        brightyState.precision === 0.5,
                                                    'bg-brandSecondary-500': brightyState.precision === 0.9,
                                                }
                                            )}
                                        >
                                            <LampIcon
                                                size="18"
                                                color={
                                                    brightyState.precision === 0.5
                                                        ? colors.brandSecondary[500]
                                                        : 'white'
                                                }
                                            />
                                            <span
                                                className={clsx('text-text-sm font-medium', {
                                                    'text-brandSecondary-500': brightyState.precision === 0.5,
                                                    'text-white': brightyState.precision === 0.9,
                                                })}
                                            >
                                                Approfondisci
                                            </span>
                                        </div>
                                    </InfoTextPopup>
                                    }
                                    {(brightyState.written || chatState.questionBotStatus === 'loading') ? (
                                        <SpinnerComponent size={30} />
                                    ) : (
                                        <ButtonComponent
                                            key={brightyState.selectedChat + '-chat-button-message'}
                                            label=""
                                            onClick={() => {
                                                sendMessage()
                                            }}
                                            color="blue"
                                            variant="solid"
                                            size="md"
                                            iconPosition="only"
                                            icon={<ArrowRightIcon size="24" color={colors.white} />}
                                            iconSize={28}
                                            disabled={errorStatus || !canSendMessage || !brightyState.textMessage.trim() || reachedLimit}
                                        />
                                    )}
                                </div>
                            </div>
                            <div key="ws-connection" style={{ display: 'none' }}>
                                {keycloak.hasRealmRole('financial_advisor') && <BrightyWebSocket
                                    key="brighty-ws"
                                    clientId={clientId}
                                    onMessage={handleWebSocketMessage}
                                    onConnect={handleConnect}
                                    onDisconnect={handleDisconnect}
                                    ref={setWsInstance}
                                />}
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    )
}
